import {
  AfterViewInit,
  ApplicationRef,
  Component,
  ComponentRef,
  ElementRef,
  EmbeddedViewRef,
  EnvironmentInjector,
  OnDestroy,
  Type,
  ViewChild,
  createComponent,
  effect,
  inject,
  input,
  output,
  untracked,
} from '@angular/core';

import { CamSchedulerWidgetComponent } from '../../engine/components/scheduler-widget/scheduler-widget.component';
import { CamEventRecord } from '../../engine/models/event-record';
import {
  CamColumnConfig,
  CamColumnRenderData,
  CamEventConfig,
  CamExternalEventSourceConfig,
  CamResourceTimeRangeConfig,
} from '../../engine/types';
import { ComponentRendererService } from '../../services/component-renderer.service';
import { CamTranslationCalendar } from '../../translation.service';
import { CamAddEventRequest, CamCalendarRessourceModel } from '../../types/calendar';
import { AbstractSchedulerComponent } from '../abstract-scheduler.component';

@Component({
  selector: 'cam-scheduler',
  imports: [CamSchedulerWidgetComponent],
  templateUrl: './scheduler.component.html',
  styleUrl: './scheduler.component.scss',
})
export class CamSchedulerComponent extends AbstractSchedulerComponent implements AfterViewInit, OnDestroy {
  events = input.required<CamEventConfig[]>();

  resources = input.required<CamCalendarRessourceModel[]>();

  resourceTimeRanges = input<CamResourceTimeRangeConfig[]>([]);

  ressourceCard = input.required<Type<unknown> | null>();

  cardComponent = input.required<Type<{ event: unknown }>>();

  externalEventSourceFeature = input<CamExternalEventSourceConfig | boolean>(false);

  addEventRequest = output<CamAddEventRequest>();

  @ViewChild('schedulerRef', { static: true })
  schedulerRef!: CamSchedulerWidgetComponent;

  @ViewChild('schedulerContainer', { static: true })
  schedulerContainer!: ElementRef<HTMLDivElement>;

  columns: CamColumnConfig[] = [];

  readonly workingTime = { fromHour: 7, toHour: 21 };

  protected readonly _translationSource = CamTranslationCalendar.getInstance();

  private readonly _componentRenderer = inject(ComponentRendererService);
  private readonly _appRef = inject(ApplicationRef);
  private readonly _environmentInjector = inject(EnvironmentInjector);
  private readonly _eventCardRefs: ComponentRef<unknown>[] = [];
  private readonly _resourceCells: HTMLElement[] = [];

  constructor() {
    super();
    effect(() => {
      this.events();
      untracked(() => this._destroyEventCards());
    });
    effect(() => {
      this.resources();
      untracked(() => this._destroyResourceCells());
    });
  }

  override ngOnInit() {
    super.ngOnInit();

    const ressourceCard = this.ressourceCard();
    this.columns = [
      {
        field: 'name',
        width: 220,
        renderer: ressourceCard ? this._resourceCellRenderer(ressourceCard) : null,
      },
    ];
  }

  ngAfterViewInit() {
    if (!this.schedulerRef) {
      return;
    }
    const scheduler = this.schedulerRef.instance;
    if (scheduler) {
      this._scheduler.setApi(scheduler, this.schedulerContainer, this.externalEventSourceFeature());

      this._registerSubscription(
        this._scheduler.addEventRequest$.subscribe({
          next: event => this.addEventRequest.emit(event),
        })
      );
    }
  }

  public eventRenderer = ({ eventRecord }: { eventRecord: CamEventRecord }) => {
    const wrapper = document.createElement('div');

    const cmpRef = createComponent(this.cardComponent(), {
      environmentInjector: this._environmentInjector,
    });

    cmpRef.setInput('event', eventRecord.data);
    this._appRef.attachView(cmpRef.hostView);
    this._eventCardRefs.push(cmpRef);

    const domElem = (cmpRef.hostView as EmbeddedViewRef<any>).rootNodes[0];
    wrapper.appendChild(domElem);

    return wrapper;
  };

  override ngOnDestroy() {
    super.ngOnDestroy();
    this._destroyEventCards();
    this._destroyResourceCells();
    this._scheduler.destroy();
  }

  private _resourceCellRenderer(ressourceCard: Type<unknown>) {
    const render = this._componentRenderer.render(ressourceCard);
    return (renderData: CamColumnRenderData) => {
      const cell = render(renderData);
      this._resourceCells.push(cell);
      return cell;
    };
  }

  private _destroyEventCards(): void {
    this._eventCardRefs.forEach(ref => ref.destroy());
    this._eventCardRefs.length = 0;
  }

  private _destroyResourceCells(): void {
    this._resourceCells.forEach(cell => this._componentRenderer.cleanup(cell));
    this._resourceCells.length = 0;
  }
}
